'use client';

import * as React from 'react';
import {cn} from '@/lib/utils';

// Everything but the root comes straight from the design system. The root is the one local
// divergence: a drag-to-pan scroll container for wide tables (#463, #487) and a hook to style that
// container (`containerClassName`, used for a sticky header). The package's `Table` renders its own
// plain container with no way to attach handlers to it. A candidate to propose upstream once a
// second app needs it; until then it is PRI's, which is why it keeps the package's markup and
// `data-slot`s exactly so a future swap is a one-line import change.
export {
  TableBody,
  TableCaption,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow
} from '@kroll-resolver-crisp/ui-components-react';

const DRAG_THRESHOLD_PX = 4;

function Table({
  className,
  containerClassName,
  ...props
}: React.ComponentProps<'table'> & {readonly containerClassName?: string}) {
  const dragRef = React.useRef<{x: number; left: number} | null>(null);
  const movedRef = React.useRef(false);
  const [panning, setPanning] = React.useState(false);

  // Mouse-only drag-to-pan. The wheel is deliberately left alone: a browser can't tell a mouse
  // from a trackpad, so turning vertical wheel into horizontal pan traps one of them (#463).
  function handlePointerDown(event: React.PointerEvent<HTMLDivElement>) {
    // Reset first: a pan that ended off-table fires no click, so the flag would otherwise go stale.
    movedRef.current = false;
    if (
      event.pointerType !== 'mouse' ||
      event.button !== 0 ||
      (event.target as HTMLElement).closest('a,button,input,select,textarea,label,[role=checkbox]')
    ) {
      return;
    }

    dragRef.current = {x: event.clientX, left: event.currentTarget.scrollLeft};
    // Suppress text selection from the first pixel, not only once the pan threshold is crossed.
    setPanning(true);
  }

  function handlePointerMove(event: React.PointerEvent<HTMLDivElement>) {
    const drag = dragRef.current;
    if (!drag) {
      return;
    }

    const dx = event.clientX - drag.x;
    // Below the threshold it is still a click, not a pan.
    if (!movedRef.current && Math.abs(dx) < DRAG_THRESHOLD_PX) {
      return;
    }

    if (!movedRef.current) {
      // Capture so the pan follows the cursor past the container edge.
      event.currentTarget.setPointerCapture(event.pointerId);
    }

    movedRef.current = true;
    event.currentTarget.scrollLeft = drag.left - dx;
  }

  function endDrag() {
    dragRef.current = null;
    setPanning(false);
  }

  // A pan that ends over the table still dispatches click to the shared <tr> (row click opens the
  // detail drawer), so swallow that one click.
  function handleClickCapture(event: React.MouseEvent<HTMLDivElement>) {
    if (movedRef.current) {
      movedRef.current = false;
      event.stopPropagation();
    }
  }

  return (
    <div
      data-slot="table-container"
      className={cn(
        'relative w-full overflow-x-auto',
        panning && 'select-none',
        containerClassName
      )}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
      onClickCapture={handleClickCapture}>
      <table
        data-slot="table"
        className={cn('w-full caption-bottom text-xs', className)}
        {...props}
      />
    </div>
  );
}

export {Table};
